import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { ContactTabSectionHeadingProps } from '~/scenes/PeopleModule/components/Tabs/ContactTab/components/ContactTabSectionHeading';
import { AppDispatch } from '~/store';
import { isValidOptionalPhoneNumber } from '~/utils/helper';

import {
  Country,
  EmpHomeContactType,
  deleteEmpHomeContactData,
  putEmpHomeContactData,
} from './data';

export type IFormInput = {
  fEmpPhoneHome: string;
  fEmpEmailHome: string;
  fEmpStreetAddress1?: string;
  fEmpStreetAddress2: string;
  fEmpPostalCode?: string;
  fEmpState: string;
  fEmpCity?: string;
  fEmpCountry?: string;
};

export type useHomeContractOutput = {
  form: UseFormReturn<IFormInput>;
  actions: Pick<
    ContactTabSectionHeadingProps,
    'onEdit' | 'onSave' | 'onCancel' | 'onDelete'
  >;
  readMode: boolean;
  empData: EmpHomeContactType;
  countriesList: Country[];
  isLoading: boolean;
};

export const useHomeContract = (): useHomeContractOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppDispatch = useDispatch();
  const confirm = useConfirmDialog();

  const { getEnums } = useAllEnums();
  const countriesList = useMemo(() => {
    return getEnums(EnumListKey.COUNTRY);
  }, [getEnums]);

  const [readMode, setReadMode] = useState(true);

  const {
    employee: { empHomeContact: empData, empBasicData },
    isLoading,
  } = useEmployee();

  const emptyFormData: IFormInput = {
    fEmpPhoneHome: '',
    fEmpEmailHome: '',
    fEmpStreetAddress1: '',
    fEmpStreetAddress2: '',
    fEmpPostalCode: '',
    fEmpCity: '',
    fEmpState: '',
    fEmpCountry: '',
  };

  const validationSchema = React.useMemo(() => {
    return yup.object().shape<IFormInput>({
      fEmpPhoneHome: yup
        .string()
        .defined()
        .test(
          'phoneNumberValidation',
          t('phoneInvalid'),
          isValidOptionalPhoneNumber,
        ),
      fEmpEmailHome: yup.string().email(t('laValidEmail')).defined(),
      fEmpStreetAddress1: yup.string().required(t('laFillManFields')),
      fEmpStreetAddress2: yup.string(),
      fEmpPostalCode: yup.string().required(t('laFillManFields')),
      fEmpCity: yup.string().required(t('laFillManFields')),
      fEmpState: yup.string(),
      fEmpCountry: yup.string().required(t('laFillManFields')),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);

  const form = useForm<IFormInput>({
    resolver,
    defaultValues: { ...emptyFormData, ...empData },
  });
  const { handleSubmit, reset } = form;

  const handleFormReset = (totalReset: boolean) => {
    if (totalReset) {
      reset(emptyFormData);
    } else {
      reset();
    }
    setReadMode(true);
  };

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpPhoneHome: data.fEmpPhoneHome,
        fEmpEmailHome: data.fEmpEmailHome,
        fEmpStreetAddress1: data.fEmpStreetAddress1,
        fEmpStreetAddress2: data.fEmpStreetAddress2,
        fEmpPostalCode: data.fEmpPostalCode,
        fEmpState: data.fEmpState,
        fEmpCity: data.fEmpCity,
        fEmpCountry: data.fEmpCountry,
      };
      (async () => {
        try {
          await putEmpHomeContactData(json, empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, empBasicData.fEmpId, enqueueSnackbar, handleSubmit, t]);

  const onSubmit = () => {
    handleFormSubmit();
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    confirm(t('contactDeleteConfirmation'), async (confirmed) => {
      if (confirmed) {
        try {
          await deleteEmpHomeContactData(empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          handleFormReset(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      }
    });
  };
  return {
    form,
    actions: {
      onDelete: handleDelete,
      onSave: onSubmit,
      onCancel: () => handleFormReset(false),
      onEdit: () => setReadMode(false),
    },
    countriesList,
    readMode,
    empData,
    isLoading,
  };
};
